import React, { useEffect, useState } from "react";
import { Factory, SquarePen, Trash, ClipboardList, Frown, Meh, Star, Receipt, Lock, Unlock, StickyNote, AlertCircle, PanelLeft } from "lucide-react";
import { getCustomerPaymentType } from "../api/payments.api";
import { getSlotAuditLogs } from "../api/slotAudit.api";
import { getCustomerNotes, createCustomerNote } from "../api/customerNotes.api";

const PAYMENT_TYPE_LABEL = {
  mensual: "Mensual (MRR)",
  paquete_dias: "Paquete de días",
};

const CustomerDetailCard = ({ customer, loginCount, onOpenLogs, onOpenPayments, onOpenSidebarConfig, onEdit, onDashboard, onSatisfaction, onToggleVisibleForEditor, onDelete }) => {

  const currentScore = customer.satisfactionScore ? Number(customer.satisfactionScore) : null;
  const [paymentType, setPaymentType] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [saveNoteError, setSaveNoteError] = useState(false);

  const [slotsOpen, setSlotsOpen] = useState(false);
  const [slotLogs, setSlotLogs] = useState([]);
  const [slotLogsLoading, setSlotLogsLoading] = useState(false);
  const [slotLogsError, setSlotLogsError] = useState(false);

  // History is fetched lazily the first time the section is expanded.
  const toggleSlotHistory = () => {
    const next = !slotsOpen;
    setSlotsOpen(next);
    if (!next) return;
    setSlotLogsLoading(true);
    setSlotLogsError(false);
    getSlotAuditLogs(customer.customerId)
      .then((data) => setSlotLogs(Array.isArray(data) ? data : []))
      .catch(() => setSlotLogsError(true))
      .finally(() => setSlotLogsLoading(false));
  };

  useEffect(() => {
    let ignore = false;
    getCustomerPaymentType(customer.customerId)
      .then((type) => { if (!ignore) setPaymentType(type); })
      .catch(() => { if (!ignore) setPaymentType(null); });
    return () => { ignore = true; };
  }, [customer.customerId]);

  useEffect(() => {
    let ignore = false;
    setNotesLoading(true);
    setNotesError(false);
    getCustomerNotes(customer.customerId)
      .then((data) => { if (!ignore) setNotes(Array.isArray(data) ? data : []); })
      .catch(() => { if (!ignore) setNotesError(true); })
      .finally(() => { if (!ignore) setNotesLoading(false); });
    return () => { ignore = true; };
  }, [customer.customerId]);

  const handleAddNote = async () => {
    const trimmed = newNote.trim();
    if (!trimmed || savingNote) return;
    setSavingNote(true);
    setSaveNoteError(false);
    try {
      const created = await createCustomerNote(customer.customerId, trimmed);
      setNotes((prev) => [created, ...prev]);
      setNewNote("");
    } catch (err) {
      setSaveNoteError(true);
    } finally {
      setSavingNote(false);
    }
  };


  console.log("CustomerDetailsCard - customer.satisfactionScore:", customer);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 transform hover:scale-[1.01]">
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Detalles del Cliente
          </h4>
          {paymentType && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
              paymentType.type === "mensual"
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
            }`}>
              {PAYMENT_TYPE_LABEL[paymentType.type] || paymentType.type}
            </span>
          )}
        </div>

        <div className="flex flex-col space-y-4">
          {/* ... campos de información (ID, Contacto, Mail, etc.) se mantienen igual ... */}
          {[
            { label: "ID del Cliente", value: customer.customerId },
            { label: "Contacto Principal", value: customer.mainUserName },
            { label: "Mail Principal", value: customer.mainUserEmail },
            { label: "Teléfono Principal", value: customer.mainUserPhone },
            { label: "Mostrar Trabajos", value: customer.showWork ? "Si" : "No" },
            { label: "Fuente del recurso", value: customer.sourceType || "No especificada" },
            { label: "Terminos y Condiciones", value: customer.termsAndConditions ? "Aceptado" : "Rechazado" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-indigo-500 dark:text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 9a1 1 0 11-2 0 1 1 0 012 0zm4 0a1 1 0 11-2 0 1 1 0 012 0zm-1 2a2 2 0 10-4 0h4z" />
              </svg>
              <span className="font-medium text-gray-700 dark:text-gray-200">{item.label}:</span>
              <span className="ml-2 font-mono">{item.value}</span>
            </div>
          ))}

          {/* ... Instagram link ... */}

          <div className="text-sm text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700 dark:text-gray-200">Slots:</span>
              <span className="font-mono">
                {customer.activeCameras ?? 0} / {customer.enabledSlots ?? "sin límite"}
              </span>
              <button
                type="button"
                onClick={toggleSlotHistory}
                aria-expanded={slotsOpen}
                className="ml-2 text-xs font-bold text-blue-600 hover:underline"
              >
                {slotsOpen ? "Ocultar historial de slots" : "Ver historial de slots"}
              </button>
            </div>
            {slotsOpen && (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs">
                {slotLogsLoading && <p className="text-gray-400">Cargando...</p>}
                {!slotLogsLoading && slotLogsError && (
                  <p className="text-red-600">No se pudo obtener el historial de slots.</p>
                )}
                {!slotLogsLoading && !slotLogsError && slotLogs.length === 0 && (
                  <p className="text-gray-400 italic">Sin cambios registrados.</p>
                )}
                {!slotLogsLoading && !slotLogsError && slotLogs.map((log, idx) => (
                  <div key={log.id ?? idx} className="flex justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded p-1.5">
                    <span>
                      {log.previousValue ?? "—"} → {log.newValue ?? "—"} · {log.actorEmail}
                    </span>
                    <span className="font-mono text-gray-400">
                      {new Date(log.createdAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr className="my-4 border-gray-200 dark:border-gray-700" />

          {/* Sección de Satisfacción con nombre de función corregido */}
          <div className="flex items-center gap-4 bg-white dark:bg-gray-900 p-2 px-4 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm w-fit">
          <span className="text-xs font-bold text-gray-400 uppercase mr-2">Satisfacción del cliente:</span>
          
          {/* Botón Enojado (1) */}
          <button 
            onClick={() => onSatisfaction(customer.customerId, 1)}
            className={`p-2.5 lg:p-1 rounded-full transition-all duration-200 hover:scale-125 ${
              currentScore === 1 
              ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" 
              : "text-gray-400 hover:text-red-400"
            }`}
            title="Enojado (1)"
          >
            <Frown className={`w-6 h-6 ${currentScore === 1 ? "fill-red-100/50" : ""}`} />
          </button>

          {/* Botón Neutral (5) */}
          <button 
            onClick={() => onSatisfaction(customer.customerId, 5)}
            className={`p-2.5 lg:p-1 rounded-full transition-all duration-200 hover:scale-125 ${
              currentScore === 5 
              ? "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400" 
              : "text-gray-400 hover:text-yellow-400"
            }`}
            title="Neutral (5)"
          >
            <Meh className={`w-6 h-6 ${currentScore === 5 ? "fill-yellow-100/50" : ""}`} />
          </button>

          {/* Botón Estrella (10) */}
          <button 
            onClick={() => onSatisfaction(customer.customerId, 10)}
            className={`p-2.5 lg:p-1 rounded-full transition-all duration-200 hover:scale-125 ${
              currentScore === 10 
              ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 shadow-sm" 
              : "text-gray-400 hover:text-amber-500"
            }`}
            title="Excelente (10)"
          >
            <Star className={`w-6 h-6 ${currentScore === 10 ? "fill-current" : ""}`} />
          </button>
        </div>

          <hr className="my-4 border-gray-200 dark:border-gray-700" />

          {/* Sección de Notas internas */}
          <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-bold text-gray-400 uppercase">Notas internas</span>
            </div>

            {notesLoading && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            )}

            {!notesLoading && notesError && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-medium py-2">
                <AlertCircle className="w-4 h-4" />
                No se pudieron obtener las notas.
              </div>
            )}

            {!notesLoading && !notesError && notes.length === 0 && (
              <p className="text-gray-400 dark:text-gray-500 italic text-sm py-2">Sin notas registradas.</p>
            )}

            {!notesLoading && !notesError && notes.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2 mb-3 pr-1">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-gray-700 dark:text-gray-200 text-xs">{note.user}</span>
                      <span className="text-[11px] text-gray-400 font-mono">
                        {new Date(note.date).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">{note.note}</p>
                  </div>
                ))}
              </div>
            )}

            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Agregar una nota interna..."
              className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
              rows={2}
            />

            {saveNoteError && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-medium mt-1">
                <AlertCircle className="w-3.5 h-3.5" />
                No se pudo guardar la nota. Intentá de nuevo.
              </div>
            )}

            <div className="flex justify-end mt-2">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || savingNote}
                className="min-h-[44px] lg:min-h-0 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 hover:bg-blue-200 text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingNote ? "Agregando..." : "Agregar nota"}
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-4">


            {/* ... botones de logs, factory, edit, trash ... */}
            <button onClick={() => onOpenLogs(customer)} className="p-3 lg:p-2 rounded-full bg-amber-100 hover:bg-amber-200 relative">
               <ClipboardList className="w-5 h-5 text-amber-600" />
            </button>
            <button onClick={() => onOpenPayments(customer)} className="p-3 lg:p-2 rounded-full bg-purple-100 hover:bg-purple-200 relative">
               <Receipt className="w-5 h-5 text-purple-600" />
            </button>
            <button onClick={() => onOpenSidebarConfig(customer)} className="p-3 lg:p-2 rounded-full bg-indigo-100 hover:bg-indigo-200 relative" title="Barra lateral">
               <PanelLeft className="w-5 h-5 text-indigo-600" />
            </button>
            <button onClick={onDashboard} className="p-3 lg:p-2 rounded-full bg-blue-100 hover:bg-green-200">
               <Factory className="w-5 h-5 text-blue-600" />
            </button>
            <button
              onClick={() => onToggleVisibleForEditor(customer)}
              className={`p-3 lg:p-2 rounded-full ${customer.visibleForEditor ? "bg-green-100 hover:bg-green-200" : "bg-red-100 hover:bg-red-200"}`}
              title={customer.visibleForEditor ? "Visible para editor (click para ocultar)" : "Oculto para editor (click para mostrar)"}
            >
              {customer.visibleForEditor ? <Unlock className="w-5 h-5 text-green-600" /> : <Lock className="w-5 h-5 text-red-600" />}
            </button>
            <button onClick={() => onEdit(customer)} className="p-3 lg:p-2 rounded-full bg-blue-100 hover:bg-blue-200">
               <SquarePen className="w-5 h-5 text-blue-600" />
            </button>
            <button onClick={() => onDelete(customer)} className="p-3 lg:p-2 rounded-full bg-red-100 hover:bg-red-200">
               <Trash className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailCard;